import { useState } from "react";

import { Button, type Column, EmptyState, Modal, Pill, Table } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import {
  useCreateTese,
  useDeleteTese,
  useTeses,
  useUpdateTese,
} from "@/hooks/useTeses";
import type { Tese, TeseCreatePayload, TeseUpdatePayload } from "@/types/tese";

const SLUG_RE = /^[a-z0-9_]+$/;

export function TesesPanel() {
  const { data: teses = [], isLoading } = useTeses();
  const remove = useDeleteTese();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Tese | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function del(t: Tese) {
    if (!confirm(`Excluir a tese "${t.nome}"?`)) return;
    setError(null);
    try {
      await remove.mutateAsync(t.id);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  const columns: Column<Tese>[] = [
    { id: "nome", header: "Tese", cell: (t) => <span className="razao">{t.nome}</span> },
    { id: "slug", header: "Slug", cell: (t) => <span className="cnpj">{t.slug}</span> },
    {
      id: "descricao",
      header: "Descrição",
      cell: (t) => <span className="td-faded">{t.descricao ?? "—"}</span>,
    },
    {
      id: "active",
      header: "Status",
      cell: (t) => <Pill tone={t.active ? "ativa" : "pausada"}>{t.active ? "Ativa" : "Inativa"}</Pill>,
    },
  ];

  return (
    <div className="lgpd-panel">
      <div className="emp-toolbar">
        <span className="emp-toolbar-counter">{teses.length} teses</span>
        <div className="emp-toolbar-spacer" />
        <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
          Nova tese
        </Button>
      </div>
      {error && <p className="td-danger">{error}</p>}

      <Table
        rows={teses}
        columns={columns}
        getKey={(t) => String(t.id)}
        onRowClick={setEditing}
        rowAction={(t) => (
          <Button
            variant="ghost"
            size="sm"
            iconLeft="x"
            disabled={remove.isPending}
            onClick={() => void del(t)}
          />
        )}
        empty={
          <EmptyState
            icon="file-text"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhuma tese cadastrada"}
            description={isLoading ? undefined : "Cadastre as teses tributárias da plataforma."}
          />
        }
      />

      {creating && <TeseModal onClose={() => setCreating(false)} />}
      {editing && <TeseModal tese={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TeseModal({ tese, onClose }: { tese?: Tese; onClose: () => void }) {
  const create = useCreateTese();
  const update = useUpdateTese();
  const isEdit = tese != null;

  const [nome, setNome] = useState(tese?.nome ?? "");
  const [slug, setSlug] = useState(tese?.slug ?? "");
  const [descricao, setDescricao] = useState(tese?.descricao ?? "");
  const [active, setActive] = useState(tese?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const slugValid = SLUG_RE.test(slug);
  const valid = nome.trim() !== "" && slugValid;
  const pending = create.isPending || update.isPending;

  async function submit() {
    if (!valid) return;
    setError(null);
    try {
      if (isEdit) {
        const payload: TeseUpdatePayload = {
          nome: nome.trim(),
          slug,
          descricao: descricao.trim() || null,
          active,
        };
        await update.mutateAsync({ id: tese.id, payload });
      } else {
        const payload: TeseCreatePayload = {
          nome: nome.trim(),
          slug,
          descricao: descricao.trim() || undefined,
          active,
        };
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={isEdit ? "Editar tese" : "Nova tese"}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!valid || pending} onClick={() => void submit()}>
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </>
      }
    >
      <div className="lgpd-detail">
        {error && <p className="td-danger">{error}</p>}
        <label className="lgpd-field">
          <span>Nome</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={200} />
        </label>
        <label className="lgpd-field">
          <span>Slug (a-z, 0-9, _)</span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="exclusao_icms_pis_cofins"
          />
        </label>
        {slug !== "" && !slugValid && (
          <p className="td-danger">Slug deve conter apenas letras minúsculas, números e _.</p>
        )}
        <label className="lgpd-field">
          <span>Descrição</span>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={3}
            maxLength={2000}
          />
        </label>
        <label className="lgpd-detail-row">
          <span>Ativa</span>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        </label>
      </div>
    </Modal>
  );
}
